import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, hoy } from '../utils/formato';

// Lo que de verdad rinde cada inmueble.
//
// Es la pantalla que justifica el programa. Un cuaderno anota los pagos; lo que
// aquí se agrega es restar lo que nadie resta:
//
//   · los ARREGLOS, cargados al inmueble y no al mes en que se pagaron;
//   · el PREDIAL y la ADMINISTRACIÓN, que corren aunque esté vacío;
//   · y sobre todo la VACANCIA, que no aparece en ninguna cuenta porque no es
//     un gasto sino plata que no entró.
//
// Sin esas tres, un local que «renta 24 millones al año» puede estar dejando 14
// y su dueño no tiene cómo saberlo.

const primeroDelAnio = () => new Date().toISOString().slice(0, 4) + '-01-01';

function Rendimiento() {
  const [datos, setDatos] = useState(null);
  const [desde, setDesde] = useState(primeroDelAnio());
  const [hasta, setHasta] = useState(hoy());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    axios.get(`/api/rendimiento?desde=${desde}&hasta=${hasta}`)
      .then(r => setDatos(r.data))
      .catch(() => setError('No se pudo calcular el rendimiento'))
      .finally(() => setCargando(false));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  const atajoAnio = (atras) => {
    const a = new Date().getFullYear() - atras;
    setDesde(`${a}-01-01`);
    setHasta(atras === 0 ? hoy() : `${a}-12-31`);
  };

  const t = datos?.totales || {};

  return (
    <>
      <div className="encabezado">
        <h1>Rendimiento</h1>
        <p>Qué deja cada inmueble después de todo</p>
      </div>

      {error && <div className="aviso malo">{error}</div>}

      <div className="bloque">
        <h2>📅 Periodo</h2>
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
          <div className="acciones">
            <button className="boton suave chico" onClick={() => atajoAnio(0)}>Este año</button>
            <button className="boton suave chico" onClick={() => atajoAnio(1)}>El año pasado</button>
            <button className="boton suave chico"
                    onClick={() => { setDesde(hoy().slice(0, 7) + '-01'); setHasta(hoy()); }}>
              Este mes
            </button>
          </div>
        </div>
      </div>

      {cargando ? (
        <p style={{ color: 'var(--tinta-tenue)' }}>Calculando…</p>
      ) : !datos ? null : (
        <>
          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--venta)' }}>
              <div className="rotulo">📥 Cobrado</div>
              <div className="valor cifra">{plata(t.cobrado)}</div>
              <div className="pie">lo que entró de verdad</div>
            </div>
            <div className="dato" style={{ '--color': 'var(--malo)' }}>
              <div className="rotulo">🔧 Gastos</div>
              <div className="valor cifra">{plata((t.arreglos || 0) + (t.otros_gastos || 0) + (t.fijos || 0))}</div>
              <div className="pie">arreglos, predial y administración</div>
            </div>
            <div className="dato" style={{ '--color': t.neto >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
              <div className="rotulo">💰 Quedó</div>
              <div className="valor cifra">{plata(Math.abs(t.neto || 0))}</div>
              <div className="pie">
                {t.potencial > 0
                  ? `${Math.round((t.neto || 0) / t.potencial * 100)}% de lo que podía dejar`
                  : '—'}
              </div>
            </div>
            <div className="dato" style={{ '--color': t.dias_vacio ? 'var(--ojo)' : 'var(--bueno)' }}>
              <div className="rotulo">🕳️ Dejó de entrar</div>
              <div className="valor cifra">{plata(t.costo_vacancia)}</div>
              <div className="pie">{t.dias_vacio} días vacíos</div>
            </div>
          </div>

          {t.costo_vacancia > 0 && (
            <div className="aviso ojo">
              <strong>Los días vacíos costaron {plata(t.costo_vacancia)}</strong> — no es un
              gasto que aparezca en ninguna factura, es plata que no entró. Es la cifra
              que casi nadie cuenta y la que más pesa.
            </div>
          )}

          <div className="bloque">
            <h2>🏢 Inmueble por inmueble</h2>
            {datos.inmuebles.length === 0 ? (
              <div className="vacio">No hay inmuebles todavía.</div>
            ) : (
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Inmueble</th>
                      <th style={{ textAlign: 'right' }}>Cobrado</th>
                      <th style={{ textAlign: 'right' }}>Arreglos</th>
                      <th style={{ textAlign: 'right' }}>Predial y adm.</th>
                      <th style={{ textAlign: 'right' }}>Quedó</th>
                      <th style={{ textAlign: 'right' }}>Vacío</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.inmuebles.map(i => (
                      <tr key={i.id}>
                        <td className="recio">
                          {i.nombre}
                          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                            {i.tipo}
                          </div>
                        </td>
                        <td className="num">{plata(i.cobrado)}</td>
                        <td className="num">{i.arreglos > 0 ? plata(i.arreglos) : '—'}</td>
                        <td className="num">{i.fijos > 0 ? plata(i.fijos) : '—'}</td>
                        <td className="num recio"
                            style={{ color: i.neto < 0 ? 'var(--malo)' : undefined }}>
                          {plata(i.neto)}
                          {i.rentabilidad_pct != null && (
                            <div style={{ fontSize: 11, fontWeight: 400,
                                          color: 'var(--tinta-tenue)' }}>
                              {i.rentabilidad_pct}% anual
                            </div>
                          )}
                        </td>
                        <td className="num">
                          {i.dias_vacio > 0 ? (
                            <>
                              <span className="pinta ojo">{i.pct_vacio}%</span>
                              <div style={{ fontSize: 11, color: 'var(--malo)', marginTop: 3 }}>
                                −{plata(i.costo_vacancia)}
                              </div>
                            </>
                          ) : <span className="pinta bueno">lleno</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="cuerpo" style={{ paddingTop: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                La <strong>rentabilidad anual</strong> es lo que quedó comparado con lo que
                costó el inmueble. Solo sale si se puso cuánto costó, en la ficha del
                inmueble. Lo de «dejó de entrar» es una estimación con el canon del último
                contrato: aproximada, pero no tenerla es peor.
              </p>
            </div>
          </div>

          {datos.inmuebles.length > 1 && (
            <div className="bloque">
              <h2>💡 Lo que hay que mirar</h2>
              <div className="cuerpo">
                {(() => {
                  const mejor = datos.inmuebles[0];
                  const peor = datos.inmuebles[datos.inmuebles.length - 1];
                  const masVacio = [...datos.inmuebles]
                    .sort((a, b) => b.costo_vacancia - a.costo_vacancia)[0];
                  return (
                    <ul style={{ margin: 0, fontSize: 15 }}>
                      <li>
                        El que más deja es <strong>{mejor.nombre}</strong> con{' '}
                        {plata(mejor.neto)}
                        {mejor.rentabilidad_pct != null && ` (${mejor.rentabilidad_pct}% anual)`}.
                      </li>
                      {peor.id !== mejor.id && (
                        <li>
                          El que menos, <strong>{peor.nombre}</strong> con {plata(peor.neto)}
                          {peor.neto < 0 && ' — está costando más de lo que produce'}.
                        </li>
                      )}
                      {masVacio.costo_vacancia > 0 && (
                        <li>
                          <strong>{masVacio.nombre}</strong> estuvo vacío {masVacio.dias_vacio}{' '}
                          días: dejó de entrar {plata(masVacio.costo_vacancia)}. Bajarle el
                          canon para llenarlo puede dejar más que tenerlo caro y vacío.
                        </li>
                      )}
                    </ul>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default Rendimiento;
